/**
 * Chart Configuration - Shared theme for all EAS Calculator charts
 *
 * Uses Powertron brand colors and dark theme styling to match the dashboard.
 */

// Brand color palette - DARK MODE (soft pastels work on dark backgrounds)
export const chartColors = {
  // Primary brand
  primary: '#A0CE4E',
  primaryLight: '#C5E87D',
  primaryDark: '#7CB342',

  // Accent colors
  teal: '#4ECDC4',
  tealLight: '#76E4DB',
  orange: '#FF6B35',
  orangeLight: '#FF8F66',
  blue: '#63B3ED',
  blueLight: '#90CDF4',

  // Semantic colors
  success: '#4CAF50',
  successLight: '#81C784',
  warning: '#FF9800',
  warningLight: '#FFB74D',
  error: '#EF5350',
  errorLight: '#E57373',

  // Neutral
  grey: '#8C9BA5',
  greyLight: '#A8B5BF',
  white: '#F4F6F8',

  // Background
  background: 'rgba(26, 31, 37, 0.95)',
  backgroundLight: 'rgba(36, 41, 49, 0.95)',
  gridLine: 'rgba(255, 255, 255, 0.08)',
  gridLineHover: 'rgba(255, 255, 255, 0.15)',
};

// LIGHT MODE colors - darker for contrast on white backgrounds (WCAG AA compliant)
// Based on Atlassian Design System and financial dashboard best practices
export const chartColorsLight = {
  // Primary brand - Atlassian success green
  primary: '#5B7F24',      // 5.2:1 contrast (Atlassian success green)
  primaryLight: '#6B9B3D',
  primaryDark: '#3D6A1C',

  // Accent colors - darker variants
  teal: '#0D7377',         // 5.8:1 contrast (was #4ECDC4 at 2.1:1)
  tealLight: '#1A9A9F',
  orange: '#C74600',       // 4.6:1 contrast (was #FF6B35 at 2.8:1)
  orangeLight: '#E05A1A',
  blue: '#1868DB',         // 4.9:1 contrast (Atlassian primary blue)
  blueLight: '#2D8AD4',

  // Semantic colors - WCAG AA compliant
  success: '#2E7D32',      // 5.7:1 contrast (was #4CAF50 at 3.0:1)
  successLight: '#388E3C',
  warning: '#B45309',      // 4.5:1 contrast (was #FF9800 at 2.9:1)
  warningLight: '#D97706',
  error: '#C62828',        // 5.2:1 contrast
  errorLight: '#D32F2F',

  // Neutral - darker greys for light backgrounds
  grey: '#5A6570',
  greyLight: '#6B7680',
  white: '#1A1F25',        // Inverted for light mode (dark text)

  // Background - light mode backgrounds
  background: 'rgba(248, 249, 250, 0.95)',
  backgroundLight: 'rgba(255, 255, 255, 0.95)',
  gridLine: 'rgba(0, 0, 0, 0.08)',
  gridLineHover: 'rgba(0, 0, 0, 0.15)',
};

/**
 * Get chart colors based on theme mode
 * @param {string} mode - 'light' or 'dark'
 * @returns {object} Appropriate color palette
 */
export const getChartColors = (mode) =>
  mode === 'light' ? chartColorsLight : chartColors;

// Semantic color mapping — use meaning, not arbitrary colors
export const semanticChartColors = {
  savings: chartColors.primary,           // green: financial gains
  roi: chartColors.primaryLight,          // light green: ROI
  energy: chartColors.teal,              // teal: energy reduction
  environmental: chartColors.success,     // green: CO2, sustainability
  info: chartColors.blue,                // blue: neutral data, coverage
  analysis: '#9F7AEA',                   // purple: ML, research
  attention: chartColors.orange,          // orange: comparisons
  cost: chartColors.error,               // red: losses, degradation
  benchmark: chartColors.warning,         // yellow: baselines
};

export const semanticChartColorsLight = {
  savings: chartColorsLight.primary,
  roi: chartColorsLight.primaryLight,
  energy: chartColorsLight.teal,
  environmental: chartColorsLight.success,
  info: chartColorsLight.blue,
  analysis: '#6B46C1',
  attention: chartColorsLight.orange,
  cost: chartColorsLight.error,
  benchmark: chartColorsLight.warning,
};

export const getSemanticChartColors = (mode) =>
  mode === 'light' ? semanticChartColorsLight : semanticChartColors;

// Gradient generators for chart fills
export const createGradient = (ctx, colorStart, colorEnd) => {
  const gradient = ctx.createLinearGradient(0, 0, 0, 400);
  gradient.addColorStop(0, colorStart);
  gradient.addColorStop(1, colorEnd);
  return gradient;
};

// Chart.js default options for dark theme
export const chartDefaults = {
  responsive: true,
  maintainAspectRatio: false,
  animation: {
    duration: 750,
    easing: 'easeOutQuart',
  },
  plugins: {
    legend: {
      display: true,
      position: 'top',
      align: 'end',
      labels: {
        color: chartColors.white,
        font: {
          family: '"Plus Jakarta Sans", sans-serif',
          size: 12,
          weight: 500,
        },
        padding: 16,
        usePointStyle: true,
        pointStyle: 'circle',
      },
    },
    tooltip: {
      enabled: true,
      backgroundColor: chartColors.background,
      titleColor: chartColors.white,
      bodyColor: chartColors.grey,
      borderColor: chartColors.gridLine,
      borderWidth: 1,
      padding: 12,
      cornerRadius: 8,
      titleFont: {
        family: '"Plus Jakarta Sans", sans-serif',
        size: 13,
        weight: 600,
      },
      bodyFont: {
        family: '"JetBrains Mono", monospace',
        size: 12,
      },
      displayColors: true,
      boxPadding: 4,
    },
  },
  scales: {
    x: {
      grid: {
        color: chartColors.gridLine,
        drawBorder: false,
      },
      ticks: {
        color: chartColors.grey,
        font: {
          family: '"Plus Jakarta Sans", sans-serif',
          size: 11,
        },
        padding: 8,
      },
      border: {
        display: false,
      },
    },
    y: {
      grid: {
        color: chartColors.gridLine,
        drawBorder: false,
      },
      ticks: {
        color: chartColors.grey,
        font: {
          family: '"JetBrains Mono", monospace',
          size: 11,
        },
        padding: 8,
      },
      border: {
        display: false,
      },
    },
  },
};

// Currency formatter for tooltips and labels
export const formatCurrency = (value) => {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  }
  return `$${value.toFixed(0)}`;
};

// Percentage formatter
export const formatPercent = (value) => {
  return `${value.toFixed(0)}%`;
};

// Number formatter
export const formatNumber = (value, decimals = 0) => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
};

// Chart container style (for wrapping Box)
export const chartContainerStyle = {
  height: 350,
  p: 3,
  background: (theme) => theme.palette.background.paper,
  border: (theme) => `1px solid ${theme.palette.divider}`,
  borderRadius: 2,
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: 'linear-gradient(90deg, #A0CE4E, #4ECDC4)',
    borderRadius: '8px 8px 0 0',
  },
};
